import React, { useState, useEffect } from 'react';
import {
  getHeroContent, StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableText, InlineEditableImage
} from './ThemeLayoutHelpers';
import {
  MdDevices, MdSecurity, MdLocalShipping, MdCached, 
  MdMenu, MdClose, MdArrowForward, MdStar, MdCompare, MdFlashOn
} from 'react-icons/md';
import { FaHeart } from 'react-icons/fa';

// Device templates for interactive specs comparison
const COMPARE_TEMPLATES = [
  {
    id: "galaxy_alpha",
    name: "Nexus Phone Alpha",
    screen: "6.8\" Dynamic AMOLED 144Hz",
    cpu: "Snapdragon 8 Gen 5 Elite",
    battery: "5200 mAh (120W HyperCharge)",
    camera: "200 MP Triple Lens with Galaxy AI",
    storage: "256GB / 512GB Ultra-Speed",
    design: "Titanium Armor Frame",
    price: "$999",
    color: "#06b6d4"
  },
  {
    id: "aero_pro",
    name: "Aero X Pro",
    screen: "6.7\" Liquid Retina XDR 120Hz",
    cpu: "A19 Bionic Pro Max",
    battery: "4800 mAh (45W MagSafe)",
    camera: "48 MP ProRAW Cinematic Master",
    storage: "128GB / 256GB / 512GB",
    design: "Aerospace Grade Titanium",
    price: "$1099",
    color: "#3b82f6"
  },
  {
    id: "horizon_fold",
    name: "Horizon V Fold",
    screen: "7.9\" Foldable FlexOLED 120Hz",
    cpu: "MediaTek Dimensity 9400",
    battery: "5000 mAh (67W Slim Charge)",
    camera: "50 MP Portrait Master Lens",
    storage: "512GB High-Density",
    design: "Carbon-Fiber Hinge Matrix",
    price: "$1499",
    color: "#a855f7"
  }
];

// Interactive slider banner contents
const HERO_SLIDES = [
  {
    eyebrow: "Next-Gen Launch",
    title: "Nexus Phone Alpha",
    subtitle: "Engineered with Titanium Armor, Snapdragon 8 Gen 5, and the ultimate 200MP cinematic lens.",
    cta: "Pre-Order Now",
    image: "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?w=1200&q=85",
    color: "#06b6d4"
  },
  {
    eyebrow: "Pure Innovation",
    title: "Aero X Pro Series",
    subtitle: "Aerospace grade build meets the power of A19 Pro chip and cinematic action camera.",
    cta: "Buy Aero Pro",
    image: "https://images.unsplash.com/photo-1510557880182-3d4d3cba35a5?w=1200&q=85",
    color: "#3b82f6"
  },
  {
    eyebrow: "Future Unfolded",
    title: "Horizon V Fold",
    subtitle: "Dual displays, carbon-fiber hinge, and extreme multitasking in a ultra-thin silhouette.",
    cta: "Discover Horizon",
    image: "https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1200&q=85",
    color: "#a855f7"
  }
];

export default function MobiTechLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#2563eb";
  const secondary = T.secondaryColor || "#3b82f6";

  const copy = getHeroContent(p, {
    eyebrow: "Next-Gen Launch",
    title: p.storeInfo?.businessName || "Nexus Phone Alpha",
    subtitle: p.storeInfo?.businessDescription || "Engineered with Titanium Armor, Snapdragon 8 Gen 5, and the ultimate 200MP cinematic lens.",
    primaryCta: "Pre-Order Now",
  });

  const getSlideData = (idx) => {
    if (idx === 0) {
      return {
        ...HERO_SLIDES[0],
        eyebrow: T.heroEyebrow || copy.eyebrow,
        title: T.heroTitle || copy.title,
        subtitle: T.heroSubtitle || copy.subtitle,
        cta: T.primaryCtaText || copy.primaryCta,
        image: p.getBannerUrl?.() || T.themePreviewImage || T.mobitech_slide_0_image || HERO_SLIDES[0].image
      };
    }
    return {
      ...HERO_SLIDES[idx],
      image: T[`mobitech_slide_${idx}_image`] || HERO_SLIDES[idx].image
    };
  };

  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  // Compare widget states
  const [deviceLeft, setDeviceLeft] = useState("galaxy_alpha");
  const [deviceRight, setDeviceRight] = useState("aero_pro");

  const leftData = COMPARE_TEMPLATES.find(d => d.id === deviceLeft) || COMPARE_TEMPLATES[0];
  const rightData = COMPARE_TEMPLATES.find(d => d.id === deviceRight) || COMPARE_TEMPLATES[1];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Automatic hero slider cycle - paused in edit mode
  useEffect(() => {
    if (p.previewEditMode) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [p.previewEditMode]);

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "catalog") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col bg-[#050811] text-slate-200 antialiased" 
      style={{ fontFamily: T.fontFamily || "Outfit, sans-serif" }}
    >
      {/* ── Top Tech Promos Banner ─────────────────────────────────── */}
      <div 
        className="w-full py-2.5 px-4 text-center text-xs font-semibold tracking-wider flex items-center justify-center gap-2 border-b border-indigo-950/40" 
        style={{ 
          background: `linear-gradient(90deg, ${primary}e6, ${secondary}e6)`, 
          color: "#fff" 
        }}
      >
        <MdFlashOn className="animate-bounce text-yellow-300" size={15} />
        <span>LIMITED TIME OFFER: Get free shipping & 1-year replacement warranty on all devices!</span>
        <button 
          onClick={(e) => navigateTo("catalog", e)} 
          className="underline hover:text-cyan-200 ml-1 font-bold inline-flex items-center gap-0.5"
        >
          Shop Devices <MdArrowForward size={12} />
        </button>
      </div>

      {/* ── Premium Glass Navigation Header ───────────────────────── */}
      <nav 
        className={`sticky top-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? "bg-[#050811]/90 backdrop-blur-xl border-b border-slate-800/80 shadow-2xl py-3" 
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border-2 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.3)]" size="h-10 w-10" />
            <div>
              <p className="text-lg font-black text-white leading-none tracking-tight">
                <InlineEditableText p={p} field="businessName" value={p.storeInfo?.businessName}>
                  {p.storeInfo?.businessName || "MOBITECH"}
                </InlineEditableText>
              </p>
              <p className="text-[10px] text-cyan-400 font-bold tracking-widest uppercase mt-1">
                {p.storeInfo?.businessCategory || "Mobile Shop"}
              </p>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["catalog", "Browse Shop"],
              ["compare", "Specs Compare"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => {
                  if (page === "compare") {
                    setActivePage("home");
                    setTimeout(() => {
                      const el = document.getElementById("compare-section");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  } else {
                    navigateTo(page, e);
                  }
                }}
                className={`text-sm font-semibold tracking-wide transition-all duration-200 relative py-1 hover:text-white ${
                  activePage === page 
                    ? "text-white" 
                    : "text-slate-400"
                }`}
              >
                {label}
                {activePage === page && (
                  <span 
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded shadow-[0_0_8px_#06b6d4]"
                    style={{ backgroundColor: primary }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-3.5">
            <button 
              onClick={p.handleGoToWishlist} 
              className="relative p-2.5 rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-950/40 text-slate-400 hover:text-red-400 transition-colors"
              title="Wishlist"
            >
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span 
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-cyan-500 animate-bounce shadow"
                >
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark />
            
            {/* Mobile Menu */}
            <button 
              className="md:hidden p-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#080d1e] border-b border-slate-800 shadow-2xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            {[
              ["home", "Home"],
              ["catalog", "Browse Shop"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => navigateTo(page, e)}
                className={`py-2 text-left text-base font-semibold border-b border-slate-900 hover:text-white ${
                  activePage === page ? "text-cyan-400" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ── Main Layout Body ───────────────────────────────────────── */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* ── Dynamic Hero Device Showcase Slider ──────────────────── */}
            <section className="max-w-7xl mx-auto px-6 pt-4 pb-8">
              <div className="relative rounded-3xl overflow-hidden bg-[#090e1a] border border-slate-800/80 shadow-[0_30px_60px_rgba(6,182,212,0.15)]">
                {/* Neon glowing ambient circles */}
                <div 
                  className="absolute -top-40 -left-40 w-96 h-96 rounded-full blur-[130px] opacity-30 pointer-events-none"
                  style={{ backgroundColor: HERO_SLIDES[activeSlide].color }}
                />
                <div 
                  className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full blur-[130px] opacity-30 pointer-events-none"
                  style={{ backgroundColor: HERO_SLIDES[activeSlide].color }}
                />

                <div className="relative min-h-[480px] md:min-h-[540px] flex flex-col md:flex-row items-center">
                  {/* Left Hero Specs & Information */}
                  <div className="flex-1 p-8 md:p-14 z-10 text-left">
                    <span 
                      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] px-3.5 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-950/20 text-cyan-400 mb-6 shadow-inner"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                      <InlineEditableText p={p} field={activeSlide === 0 ? "heroEyebrow" : `hero_eyebrow_${activeSlide}`} value={getSlideData(activeSlide).eyebrow}>
                        {getSlideData(activeSlide).eyebrow}
                      </InlineEditableText>
                    </span>
                    <h1 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight">
                      <InlineEditableText p={p} field={activeSlide === 0 ? "heroTitle" : `hero_title_${activeSlide}`} value={getSlideData(activeSlide).title}>
                        {getSlideData(activeSlide).title}
                      </InlineEditableText>
                    </h1>
                    <p className="mt-4 text-slate-400 text-sm md:text-base max-w-lg leading-relaxed">
                      <InlineEditableText p={p} field={activeSlide === 0 ? "heroSubtitle" : `hero_sub_${activeSlide}`} value={getSlideData(activeSlide).subtitle}>
                        {getSlideData(activeSlide).subtitle}
                      </InlineEditableText>
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                      <button 
                        onClick={(e) => navigateTo("catalog", e)}
                        className="px-8 py-4 rounded-2xl text-sm font-bold text-white shadow-xl hover:shadow-[0_0_20px_#06b6d4] hover:scale-105 active:scale-95 transition-all duration-300"
                        style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
                      >
                        <InlineEditableText p={p} field={activeSlide === 0 ? "primaryCtaText" : `hero_cta_${activeSlide}`} value={getSlideData(activeSlide).cta}>
                          {getSlideData(activeSlide).cta}
                        </InlineEditableText>
                      </button>
                      <button 
                        onClick={() => {
                          const el = document.getElementById("compare-section");
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="px-8 py-4 rounded-2xl text-sm font-semibold text-slate-300 border border-slate-800 bg-slate-950/40 hover:bg-slate-900 hover:border-slate-700 transition-all duration-300"
                      >
                        Compare Specs
                      </button>
                    </div>
                  </div>

                  {/* Right Device Visual */}
                  <div className="flex-1 w-full h-[280px] md:h-[540px] relative overflow-hidden self-stretch shrink-0">
                    <InlineEditableImage p={p} field={activeSlide === 0 ? "themePreviewImage" : `mobitech_slide_${activeSlide}_image`} className="h-full w-full">
                      <img 
                        src={getSlideData(activeSlide).image} 
                        alt="Flagship Phone" 
                        className="w-full h-full object-cover transition-all duration-1000 transform hover:scale-[1.03]"
                      />
                    </InlineEditableImage>
                    {/* Shadow masking fade */}
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#090e1a] via-transparent to-transparent pointer-events-none" />
                  </div>
                </div>

                {/* Slider indicators */}
                <div className="absolute bottom-6 left-8 md:left-14 flex items-center gap-3.5 z-20">
                  {HERO_SLIDES.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSlide(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        activeSlide === idx ? "w-8 bg-cyan-500" : "w-2 bg-slate-700 hover:bg-slate-500"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* ── Service Trust Badges ───────────────────────────────────── */}
            <section className="max-w-7xl mx-auto px-6 py-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Elite Hardware", desc: "100% Genuine Brands", icon: <MdDevices className="text-2xl text-cyan-400" /> },
                  { label: "Shield Security", desc: "Fully Secured Payments", icon: <MdSecurity className="text-2xl text-blue-400" /> },
                  { label: "Rapid Logistics", desc: "Insured Air Cargo Delivery", icon: <MdLocalShipping className="text-2xl text-purple-400" /> },
                  { label: "Assurance Care", desc: "1-Year Warranty Included", icon: <MdCached className="text-2xl text-emerald-400" /> },
                ].map((item, idx) => (
                  <div 
                    key={idx} 
                    className="p-5 rounded-2xl bg-slate-900/20 border border-slate-800/40 hover:border-slate-800/80 transition-all duration-300 flex items-center gap-4 text-left shadow-sm group hover:-translate-y-0.5"
                  >
                    <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 group-hover:border-cyan-500/40 transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{item.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ── INTERACTIVE SPECS COMPARISON DECK ──────────────────────── */}
            <section id="compare-section" className="max-w-7xl mx-auto px-6 py-12 scroll-mt-24">
              <div className="p-8 rounded-3xl bg-slate-950 border border-slate-800/80 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 rounded-full blur-[140px] bg-cyan-900/10 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full blur-[140px] bg-purple-900/10 pointer-events-none" />

                <div className="text-center max-w-xl mx-auto mb-8">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-cyan-400">Spec Duel</span>
                  <h2 className="text-2xl md:text-3xl font-black text-white mt-1.5">Interactive Comparison Deck</h2>
                  <p className="text-xs text-slate-400 mt-2">Side-by-side technical specs breakdown. Pick devices below to compare instant specs details.</p>
                </div>

                {/* Device Pickers */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                  <div className="flex flex-col gap-1 w-full sm:w-64">
                    <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Device One</label>
                    <select 
                      value={deviceLeft}
                      onChange={(e) => setDeviceLeft(e.target.value)}
                      className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-200 outline-none focus:ring-1 focus:ring-cyan-500 cursor-pointer"
                    >
                      {COMPARE_TEMPLATES.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="h-8 w-8 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-cyan-400 shadow mt-4">
                    <MdCompare size={16} />
                  </div>

                  <div className="flex flex-col gap-1 w-full sm:w-64">
                    <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Device Two</label>
                    <select 
                      value={deviceRight}
                      onChange={(e) => setDeviceRight(e.target.value)}
                      className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-200 outline-none focus:ring-1 focus:ring-cyan-500 cursor-pointer"
                    >
                      {COMPARE_TEMPLATES.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Spec Sheets Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left Device Spec Card */}
                  <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 text-left transition-all duration-300 hover:border-cyan-500/20">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-4 mb-4">
                      <div>
                        <h4 className="text-base font-black text-white">{leftData.name}</h4>
                        <span className="text-xs text-cyan-400 font-semibold mt-1 block">{leftData.price} MSRP</span>
                      </div>
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: leftData.color, boxShadow: `0 0 10px ${leftData.color}` }} />
                    </div>

                    <div className="space-y-4 text-xs">
                      {[
                        { label: "Display Module", val: leftData.screen },
                        { label: "Silicon Engine", val: leftData.cpu },
                        { label: "Battery & Wattage", val: leftData.battery },
                        { label: "Optics Assembly", val: leftData.camera },
                        { label: "Storage Architecture", val: leftData.storage },
                        { label: "Industrial Finish", val: leftData.design }
                      ].map((spec, i) => (
                        <div key={i} className="flex justify-between border-b border-slate-900 pb-2">
                          <span className="text-slate-500 font-semibold">{spec.label}</span>
                          <span className="text-slate-200 text-right">{spec.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Device Spec Card */}
                  <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 text-left transition-all duration-300 hover:border-purple-500/20">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-4 mb-4">
                      <div>
                        <h4 className="text-base font-black text-white">{rightData.name}</h4>
                        <span className="text-xs text-purple-400 font-semibold mt-1 block">{rightData.price} MSRP</span>
                      </div>
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: rightData.color, boxShadow: `0 0 10px ${rightData.color}` }} />
                    </div>

                    <div className="space-y-4 text-xs">
                      {[
                        { label: "Display Module", val: rightData.screen },
                        { label: "Silicon Engine", val: rightData.cpu },
                        { label: "Battery & Wattage", val: rightData.battery },
                        { label: "Optics Assembly", val: rightData.camera },
                        { label: "Storage Architecture", val: rightData.storage },
                        { label: "Industrial Finish", val: rightData.design }
                      ].map((spec, i) => (
                        <div key={i} className="flex justify-between border-b border-slate-900 pb-2">
                          <span className="text-slate-500 font-semibold">{spec.label}</span>
                          <span className="text-slate-200 text-right">{spec.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {/* ── Main Catalog Section ───────────────────────────────────── */}
        <section id="catalog-section" className="max-w-7xl mx-auto px-6 py-12 scroll-mt-24">
          <div className="border-b border-slate-800/80 pb-4 mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <span className="text-[10px] uppercase font-bold tracking-widest text-cyan-400">Inventory Catalog</span>
              <h2 className="text-2xl md:text-3xl font-black text-white mt-1">Smart Phones & Gadgets</h2>
            </div>
          </div>
          <TabContent p={p} dark />
        </section>

        {/* ── Customer Voice Reviews ─────────────────────────────────── */}
        {activePage === "home" && (
          <section className="max-w-7xl mx-auto px-6 py-12">
            <div className="text-center max-w-lg mx-auto mb-10">
              <span className="text-[10px] uppercase font-bold tracking-widest text-cyan-400">Customer Reviews</span>
              <h3 className="text-2xl md:text-3xl font-black text-white mt-1.5">What Tech Shoppers Say</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Aarav Sharma", comment: "Bought the Nexus Phone Alpha. Super fast delivery and the 120W charge is insane. Best tech purchase ever!", rate: 5 },
                { name: "Kiara Patel", comment: "The specs comparison tool on this website helped me pick the right Fold phone. Exceptional service and user interface.", rate: 5 },
                { name: "Priya Das", comment: "Authentic Apple and Samsung models with original warranty. Very glad DukanBuddy shops are so premium.", rate: 5 }
              ].map((rev, idx) => (
                <div key={idx} className="p-6 rounded-2xl bg-slate-900/20 border border-slate-800/40 text-left flex flex-col justify-between">
                  <div>
                    <div className="flex gap-1 mb-3">
                      {[...Array(rev.rate)].map((_, i) => (
                        <MdStar key={i} className="text-yellow-400 text-sm" />
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 italic leading-relaxed">"{rev.comment}"</p>
                  </div>
                  <span className="text-xs text-white font-bold block mt-4 border-t border-slate-900 pt-3">{rev.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* ── Futuristic Dark Tech Footer ────────────────────────────── */}
      <StoreFooter p={p} dark />
    </div>
  );
}
